<template>
    <div class="px-3 mt-4">
        <div class="mt-3 mb-8 space-y-2 max-w-7xl mx-auto">
            <div class="flex items-center justify-between bg-gray-50 py-2 px-3">
                <h4 class="text-lg text-center font-medium">Time #{{ id }}</h4>
                <div class="flex space-x-4 justify-end">
                    <button
                        class="flex items-center justify-center border border-gray-400 hover:border-gray-900 text-sm rounded px-6 py-2 focus:outline-none focus:shadow-outline"
                        @click="editModalOpen = true"
                    >
                        <svg
                            class="w-5 h-5 text-gray-400 mr-1"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            ></path>
                        </svg>
                        Edit
                    </button>
                    <button
                        class="group flex items-center justify-center border text-red-800 border-red-500 hover:text-white hover:bg-red-500 text-sm rounded px-6 py-2 focus:outline-none focus:shadow-outline"
                        @click="deleteModalOpen = true"
                    >
                        <svg
                            class="w-5 h-5 group-hover:text-red-100 text-red-300 mr-1"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            ></path>
                        </svg>
                        Delete
                    </button>
                </div>
            </div>
            <div v-if="time">
                <modal
                    closable="true"
                    @close="closeEditModal"
                    :title="`Update Time #${time.id}`"
                    :open="editModalOpen"
                >
                    <div slot="content">
                        <form class="space-y-4 mt-4" @submit.prevent="updateTime">
                            <div class="md:flex items-center space-y-2 md:space-y-0 md:space-x-3">
                                <section class="flex-1 space-y-1 flex flex-col">
                                    <label for="givKind" class="text-sm">Time Name (JP)</label>
                                    <input
                                        placeholder="東京"
                                        :disabled="submitting || submitted"
                                        v-model="editFormTag"
                                        name="tag"
                                        class="form-input"
                                    />
                                </section>
                                <section class="flex-1 space-y-1 flex flex-col">
                                    <label for="givKind" class="text-sm">Time Name (English)</label>
                                    <input
                                        placeholder="Tokyo"
                                        :disabled="submitting || submitted"
                                        v-model="editFormTagEn"
                                        name="tagEn"
                                        class="form-input"
                                    />
                                </section>
                            </div>
                            <div class="flex items-center space-x-3">
                                <section class="flex-1 space-y-1">
                                    <label class="flex items-center">
                                        <input
                                            type="checkbox"
                                            :disabled="submitting || submitted"
                                            v-model="editFormIsVisible"
                                            name="visible"
                                            class="form-checkbox mr-2 p-2"
                                        />
                                        <span class="">Visible</span>
                                    </label>
                                </section>
                            </div>
                            <p v-if="!!editError" class="my-2 error-msg">{{ editError }}</p>
                            <div class="mt-6 mb-2 flex justify-between">
                                <Button
                                    :disabled="submitting"
                                    @click="closeEditModal"
                                    class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                                    type="button"
                                    label="Cancel"
                                />
                                <Button
                                    :disabled="!formValid"
                                    :loading="updating"
                                    :class="[formValid ? '' : 'pointer-events-none opacity-75']"
                                    class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded"
                                    label="Update"
                                />
                            </div>
                        </form>
                    </div>
                </modal>
                <modal @close="closeDeleteModal" :closable="!deleting" :open="deleteModalOpen">
                    <div slot="header">
                        <h2 class="pt-2 text-xl font-semibold text-red-600">
                            Delete Time #{{ time.id }}
                        </h2>
                    </div>
                    <div slot="content">
                        <form class="mt-4" @submit.prevent="deleteTime">
                            <p class="bg-red-100 rounded-sm px-3 py-4">
                                Are you sure you want to delete time id #{{ time.id }}.
                                <br />
                                <span class="uppercase tracking-wide text-sm font-medium"
                                    >This action cannot be reversed!!</span
                                >
                            </p>
                            <p v-if="!!deleteError" class="my-2 error-msg">{{ deleteError }}</p>
                            <div class="px-2 mt-6 mb-2 flex justify-between">
                                <Button
                                    :disabled="deleting"
                                    @click="closeDeleteModal"
                                    class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                                    type="button"
                                    label="Cancel"
                                />
                                <Button
                                    :disabled="deleting"
                                    :loading="deleting"
                                    class="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded"
                                    label="Yes Delete"
                                />
                            </div>
                        </form>
                    </div>
                </modal>
            </div>
            <div>
                <pre><code>{{ JSON.stringify(time, null, 1) }}</code></pre>
            </div>
        </div>
    </div>
</template>
<script>
import gql from 'graphql-tag'
const time_QUERY = gql`
    query GetTime($id: Int!) {
        time(id: $id) {
            id
            tag
            tagEn
            isVisible
        }
    }
`
export default {
    methods: {
        closeEditModal() {
            this.editModalOpen = false
            this.editError = null
            this.resetData()
        },
        closeDeleteModal() {
            this.deleteModalOpen = false
            this.deleteError = null
        },
        async updateTime() {
            this.updating = true
            this.$apollo
                .mutate({
                    mutation: gql`
                        mutation($id: Int!, $data: TimeUpdateData!) {
                            updateTime(id: $id, data: $data) {
                                id
                                tag
                                tagEn
                                isVisible
                            }
                        }
                    `,
                    variables: {
                        id: this.id,
                        data: {
                            tag: this.editFormTag,
                            tagEn: this.editFormTagEn,
                            isVisible: this.editFormIsVisible,
                        },
                    },
                })
                .then(() => {
                    this.updating = false
                    this.editModalOpen = false
                })
                .catch(err => {
                    let errMsg = err.message
                    try {
                        errMsg = err.graphQLErrors[0].message
                    } catch (e) {
                        //
                    }
                    try {
                        errMsg = err.networkError.result.errors[0].message
                    } catch (e) {
                        //
                    }
                    this.updating = false
                    this.editError = errMsg
                })
        },
        async deleteTime() {
            this.deleting = true
            this.$apollo
                .mutate({
                    mutation: gql`
                        mutation($id: Int!) {
                            deleteTime(id: $id)
                        }
                    `,
                    variables: {
                        id: this.id,
                    },
                })
                .then(() => {
                    this.deleting = false
                    this.deleteModalOpen = false
                    this.$router.replace({ path: '/times', query: { reload: true } })
                })
                .catch(err => {
                    let errMsg = err.message
                    try {
                        errMsg = err.graphQLErrors[0].message
                    } catch (e) {
                        //
                    }
                    try {
                        errMsg = err.networkError.result.errors[0].message
                    } catch (e) {
                        //
                    }
                    this.deleting = false
                    this.deleteError = errMsg
                })
        },
        resetData() {
            this.editFormTag = this.time.tag
            this.editFormTagEn = this.time.tagEn
            this.editFormIsVisible = this.time.isVisible
        },
    },
    apollo: {
        time: {
            query: time_QUERY,
            variables() {
                return {
                    id: this.id,
                }
            },
        },
    },
    computed: {
        formValid() {
            return (
                this.time.tag !== this.editFormTag ||
                this.time.tagEn !== this.editFormTagEn ||
                this.time.isVisible !== this.editFormIsVisible
            )
        },
        editFormTag: {
            get() {
                const result = this.formTag || this.time.tag
                return result
            },
            set(v) {
                this.formTag = v
            },
        },
        editFormTagEn: {
            get() {
                const result = this.formTagEn || this.time.tagEn
                return result
            },
            set(v) {
                this.formTagEn = v
            },
        },
        editFormIsVisible: {
            get() {
                if (this.formIsVisible === null) {
                    return this.time.isVisible
                }
                const result = this.formIsVisible
                return result
            },
            set(v) {
                this.formIsVisible = v
            },
        },
    },
    data() {
        return {
            formTag: '',
            formTagEn: '',
            formIsVisible: null,
            deleting: false,
            updating: false,
            editModalOpen: false,
            deleteModalOpen: false,
            submitting: false,
            submitted: false,
            deleteError: null,
            editError: null,
            id: parseInt(this.$route.params.id),
        }
    },
}
</script>
